import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, RefreshCw, TrendingUp, Lock } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { adaptiveMacrosApi } from '../api/adaptiveMacros';
import { useAuth } from '../contexts/AuthContext';
import ReverseDietOnboarding from '../components/ReverseDietOnboarding';
import InfoButton from '../components/ui/info-button';

const GOALS = [
  { value: 'cut', label: 'Cut', hint: 'Lose fat while preserving muscle' },
  { value: 'maintain', label: 'Maintain', hint: 'Hold current weight' },
  { value: 'bulk', label: 'Bulk', hint: 'Gain muscle (lean surplus)' },
  { value: 'recomp', label: 'Recomp', hint: 'Slowly lose fat while gaining muscle' },
];

const AGGRESSIVENESS = [
  { value: 'slow', label: 'Slow', hint: '±50 kcal steps · bi-weekly' },
  { value: 'moderate', label: 'Moderate', hint: '±100 kcal steps · bi-weekly' },
  { value: 'aggressive', label: 'Aggressive', hint: '±150 kcal steps · weekly' },
];

const AdaptiveMacroSettingsPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const clientId = params.get('client_id') || null;
  const isCoachView = !!clientId && user?.role !== 'client';
  const coachManagedSelf = !isCoachView && (user?.coach_diet_active || (user?.coach_id && user?.macros_locked_by_coach));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [config, setConfig] = useState(null);
  const [goal, setGoal] = useState('cut');
  const [aggressiveness, setAggressiveness] = useState('moderate');
  const [enabled, setEnabled] = useState(false);
  const [reverseOnboardingOpen, setReverseOnboardingOpen] = useState(false);

  const isReverse = config?.goal === 'reverse' && config?.enabled;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await adaptiveMacrosApi.getConfig(clientId);
        if (!alive) return;
        const cfg = data?.config;
        setConfig(cfg || null);
        if (cfg) {
          setGoal(cfg.goal || 'maintain');
          setAggressiveness(cfg.aggressiveness || 'moderate');
          setEnabled(!!cfg.enabled);
        }
      } catch (e) {
        toast.error('Failed to load config');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [clientId]);

  const save = async () => {
    setSaving(true);
    try {
      if (enabled) {
        const { data } = await adaptiveMacrosApi.enable({
          goal,
          aggressiveness,
          client_id: clientId,
        });
        setConfig(data?.config || null);
        toast.success('Adaptive macros enabled');
      } else {
        await adaptiveMacrosApi.disable(clientId);
        toast.success('Adaptive macros disabled');
        setConfig((c) => c ? { ...c, enabled: false } : c);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      const { data } = await adaptiveMacrosApi.recompute({ client_id: clientId, force: true });
      if (data?.applied) {
        toast.success('Targets recomputed');
        const { data: fresh } = await adaptiveMacrosApi.getConfig(clientId);
        setConfig(fresh?.config || null);
      } else {
        toast.info('No adjustment needed — not enough data or on track');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  const daysUntil = useMemo(() => {
    if (!config?.next_adjustment_at) return null;
    const ms = new Date(config.next_adjustment_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [config?.next_adjustment_at]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6 pb-20">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Adaptive Macros</h1>
        <InfoButton contentKey="adaptiveMacros" />
      </div>

      <Card className="bg-gradient-to-br from-zinc-900 via-zinc-950 to-black border-white/10 text-white overflow-hidden relative">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#FF5C00]/20 blur-3xl" />
        <div className="relative p-5 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#FF5C00]" />
            <div className="font-semibold">Auto-tune your targets every cycle</div>
          </div>
          <p className="text-sm text-white/70">
            Your check-ins feed an algorithm that estimates your real TDEE and adjusts
            calories / macros automatically — typically every 1–2 weeks. Safer, faster
            results than static plans.
          </p>
        </div>
      </Card>

      {coachManagedSelf && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="pt-5 flex items-start gap-3">
            <Lock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-medium">Managed by your coach</div>
              <div className="text-muted-foreground">
                Adaptive macros are disabled while your coach manages your diet protocol.
                Ask your coach to deactivate your diet protocol if you want to switch to
                adaptive mode.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-full bg-[#FF5C00]/10 flex items-center justify-center shrink-0">
              <TrendingUp className="h-4 w-4 text-[#FF5C00]" />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm">Reverse Diet</div>
              <div className="text-xs text-muted-foreground truncate">
                {isReverse ? `Week ${config.reverse_config?.week_number ?? 0} in progress` : 'Guided post-cut kcal ramp-up'}
              </div>
            </div>
          </div>
          <Button
            variant={isReverse ? 'outline' : 'default'}
            size="sm"
            disabled={!isReverse && coachManagedSelf}
            onClick={() => isReverse
              ? navigate('/reverse-diet' + (clientId ? `?client_id=${clientId}` : ''))
              : setReverseOnboardingOpen(true)
            }
            className="shrink-0"
          >
            {isReverse ? 'View progress' : 'Start'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Enable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Adaptive macros</div>
              <div className="text-sm text-muted-foreground">
                {enabled ? 'Your targets update on every cycle' : 'Off — targets stay fixed'}
              </div>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} disabled={coachManagedSelf} />
          </div>

          {enabled && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label>Goal</Label>
                <RadioGroup value={goal} onValueChange={setGoal} className="grid grid-cols-2 gap-2">
                  {GOALS.map((g) => (
                    <label
                      key={g.value}
                      htmlFor={`goal-${g.value}`}
                      className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${goal === g.value ? 'border-primary bg-primary/5' : 'border-border'}`}
                    >
                      <RadioGroupItem value={g.value} id={`goal-${g.value}`} className="mt-0.5" />
                      <div className="min-w-0">
                        <div className="font-medium">{g.label}</div>
                        <div className="text-xs text-muted-foreground">{g.hint}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>Aggressiveness</Label>
                <RadioGroup value={aggressiveness} onValueChange={setAggressiveness} className="grid gap-2">
                  {AGGRESSIVENESS.map((a) => (
                    <label
                      key={a.value}
                      htmlFor={`agg-${a.value}`}
                      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${aggressiveness === a.value ? 'border-primary bg-primary/5' : 'border-border'}`}
                    >
                      <RadioGroupItem value={a.value} id={`agg-${a.value}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{a.label}</div>
                        <div className="text-xs text-muted-foreground">{a.hint}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {config?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current targets</span>
              <span className="font-medium tabular-nums">
                {config.current_targets?.kcal ?? '—'} kcal · P{config.current_targets?.protein_g ?? 0} / C{config.current_targets?.carbs_g ?? 0} / F{config.current_targets?.fat_g ?? 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estimated TDEE</span>
              <span className="font-medium tabular-nums">{config.current_tdee_estimate ?? '—'} kcal</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Next adjustment</span>
              <span className="font-medium">{daysUntil != null ? (daysUntil === 0 ? 'Due now' : `in ${daysUntil}d`) : '—'}</span>
            </div>
            {isCoachView && (
              <Button
                variant="outline"
                size="sm"
                onClick={recompute}
                disabled={recomputing}
                className="w-full mt-2"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${recomputing ? 'animate-spin' : ''}`} />
                Force recompute now
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="sticky bottom-4" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <Button
          onClick={save}
          disabled={saving || coachManagedSelf}
          className="w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white shadow-[0_0_24px_rgba(255,107,47,0.35)]"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <ReverseDietOnboarding
        open={reverseOnboardingOpen}
        onClose={() => setReverseOnboardingOpen(false)}
        onComplete={async () => {
          const { data } = await adaptiveMacrosApi.getConfig(clientId);
          setConfig(data?.config || null);
          if (data?.config) {
            setGoal(data.config.goal || 'reverse');
            setEnabled(!!data.config.enabled);
          }
        }}
        clientId={clientId}
      />
    </div>
  );
};

export default AdaptiveMacroSettingsPage;
